/**
 * The primitive field schemas, transcribed from backend.md §4.4. Everything that
 * validates a username, an email or a password anywhere in the system — the
 * Express routes, the Colyseus message handlers and the web forms — composes
 * these, so the client can never accept something the server will reject.
 */
import { z } from 'zod';
/**
 * Names the platform keeps for itself. Checked case-insensitively, because the
 * uniqueness index is on `lower(username)` (backend.md §5) and `Admin` must not
 * slip past a list that only holds `admin`.
 */
export const RESERVED_USERNAMES = new Set([
    'admin',
    'administrator',
    'moderator',
    'mod',
    'root',
    'system',
    'support',
    'help',
    'staff',
    'ordago',
    'órdago',
    'mus',
    'api',
    'www',
    'mail',
    'me',
    'you',
    'null',
    'undefined',
    'anonymous',
    'anon',
    'bot',
    'bots',
    'deleted',
    'guest',
    'invitado',
    'banned',
    'security',
    'abuse',
    'postmaster',
    'webmaster',
    'noreply',
    'no-reply',
]);
export const usernameSchema = z
    .string()
    .trim()
    .min(3)
    .max(20)
    .regex(/^[a-zA-Z0-9_]+$/, 'username_charset')
    .refine((v) => !RESERVED_USERNAMES.has(v.toLowerCase()), 'username_reserved');
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const passwordSchema = z
    .string()
    .min(10)
    .max(128)
    .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /[0-9]/.test(v), 'password_needs_mixed_case_and_digit');
export const verificationCodeSchema = z.string().regex(/^\d{6}$/);
/**
 * No I, O, 0 or 1 — these codes are read aloud across a table and typed from
 * memory, and those four are the pairs that get transcribed wrong.
 */
export const roomCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(6)
    .regex(/^[A-HJ-NP-Z2-9]{6}$/);
/** The alphabet `roomCodeSchema` accepts; the generator in M3 draws from it. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const LOCALES = ['es', 'en'];
export const localeSchema = z.enum(LOCALES);
/**
 * The display form of a username. backend.md §5 stores one `username` column
 * whose comment reads "display form preserved", with uniqueness enforced on
 * `lower(username)` — so a display name is a re-casing of the account name, not
 * a second identity. The API enforces that equivalence; this only bounds shape.
 */
export const displayNameSchema = z
    .string()
    .trim()
    .min(3)
    .max(20)
    .regex(/^[a-zA-Z0-9_]+$/);
//# sourceMappingURL=common.js.map